import type { ApiClient } from '../client';
import { mapAddress, type RawAddress, type ShippingAddress } from './addresses';

const BASE = '/loot_machine/api/game_platform/iframe/v1/pool_shippings';

export type PoolShippingType = 'delivery' | 'smc_transfer' | (string & {});
// Mirrors LootMachine::PoolShipping's `status` enum, minus `merged` — every
// iframe endpoint filters merged-away shipments out server-side, so it can
// never reach this client.
export type PoolShippingStatus =
  | 'pending'
  // Invoice generated, awaiting the player's payment — between `pending` and `paid`.
  | 'waiting_for_payment'
  | 'paid'
  // Fulfilment has started (between paid and shipped) — no longer cancelable.
  | 'request_shipping'
  | 'canceled'
  | 'shipped'
  | 'delivered'
  | (string & {});

/** Derived fulfilment summary the backend adds once a warehouse resolves the
 *  cards: `refunded` = every card was unavailable (all refunded),
 *  `partially_shipped` = some shipped, some refunded. `null` when nothing was
 *  unavailable — the list pill keeps the plain `status`. */
export type PoolShippingSettleSummary = 'refunded' | 'partially_shipped' | null;

export interface IframePoolShippingInvoice {
  id: number;
  status: string | null;
  totalAmount: number | null;
  paidAt: string | null;
}

export interface IframePoolShipping {
  id: number;
  status: PoolShippingStatus;
  /** True only while the delivery is `paid` and still cancelable — drives the
   *  Cancel delivery CTA on the Show page. */
  isAllowToCancel: boolean;
  /** Derived refund/partial summary — overrides the status pill when set. */
  settleSummary: PoolShippingSettleSummary;
  shippingType: PoolShippingType;
  humanizedStatus: string;
  createdAt: string;
  poolItemsCount: number;
  invoice: IframePoolShippingInvoice | null;
  /** First item's first image — the deliveries-list card preview. Null when
   *  the item has no image. */
  previewImageUrl: string | null;
  /** Title of the same first card — the sleek list row names it. Null on a
   *  backend that doesn't send it yet. */
  previewTitle: string | null;
}

export interface IframePoolShippingsMeta {
  totalCount: number;
  page: number;
  per: number;
  activeOutCount: number;
}

export interface IframePoolShippingsPage {
  shippings: IframePoolShipping[];
  meta: IframePoolShippingsMeta;
}

export interface IframePoolShippingsParams {
  page?: number;
  per?: number;
}

/** Item attached to a created pool_shipping. The Show page renders one
 *  card per row in the Shipping List grid. */
export type ShippingItemSettleStatus =
  | 'pending'
  | 'shipped'
  | 'unavailable'
  | 'canceled'
  | null;

export interface IframePoolShippingItem {
  id: number;
  title: string;
  imageUrl: string | null;
  fairMarketValue: number | null;
  /** Per-card fulfilment: shipped, unavailable (couldn't ship — refunded),
   *  canceled (bought back when the player canceled the delivery), or
   *  pending (warehouse hasn't resolved yet). */
  settleStatus: ShippingItemSettleStatus;
}

/** Extra paid line under the chosen shipping service — e.g. insurance,
 *  signature confirmation. Rendered with a check icon under the service
 *  card on the Show page. */
export interface IframePoolShippingPosition {
  id: number;
  name: string;
  price: number | null;
}

/** What the player actually paid at order time, in the operator (platform)
 *  currency. `shipping` already includes the chosen insurance/signature
 *  EasyPost add-on. Null when the shipment has no charge snapshot. */
export interface ShippingChargeBreakdown {
  shipping: number | null;
  /** Insurance portion carved out of `shipping` (which stays the FULL price).
   *  Null for shipments charged before the backend exposed the split. */
  insurance: number | null;
  handling: number | null;
  salesTax: number | null;
  total: number | null;
  /** The operator-funded promotion that priced this shipment — the figures
   *  above are then what the player paid. Null (or absent on a payload from
   *  before promotions) when the regular price was charged. */
  promo?: ShippingChargePromo | null;
}

/** What a promotion changed on a shipment that was already charged: the
 *  regular fees the player did not pay, and what the promotion took off. */
export interface ShippingChargePromo {
  id: number;
  label: string;
  originalShipping: number | null;
  originalHandling: number | null;
  discount: number;
}

/** Detailed view of a single pool_shipping — drives the Show page.
 *  Extends `IframePoolShipping` (the list-row shape) with the fields
 *  only the detail endpoint returns (address, items, totals, etc). */
export interface IframePoolShippingDetail extends IframePoolShipping {
  address: ShippingAddress | null;
  items: IframePoolShippingItem[];
  service: PoolShippingService | null;
  positions: IframePoolShippingPosition[];
  salesTax: number;
  totalAmount: number;
  /** Real cost the player paid (operator currency) — game_platform has no
   *  Stripe invoice, so positions carry no price. */
  chargeBreakdown: ShippingChargeBreakdown | null;
  /** Carrier tracking, shown once the shipment is shipped. */
  trackingNumber: string | null;
  companyName: string | null;
}

/** Stable identifier for a shipping service's protection tier — the
 *  backend's `code_name`. Drives the auto-select + opt-out logic
 *  (`coversInsurance` / `coversSignature`). New tiers are tolerated via
 *  the `string & {}` widening so an unknown value never crashes the UI. */
export type ShippingServiceCodeName =
  | 'delivery'
  | 'insurance'
  | 'signature'
  | 'insurance_and_signature'
  | (string & {});

/** One row from `pool_shipping_services` — rendered in the Delivery
 *  Shipping Options section. */
export interface PoolShippingService {
  id: number;
  name: string;
  description: string | null;
  /** Optional flat fee for the service in USD; surfaced as a fee row
   *  when the backend ships it. */
  price: number | null;
  /** Protection tier this option provides — maps `code_name`. Used to
   *  auto-select the required option and to detect an opt-out. */
  codeName: ShippingServiceCodeName;
}

/**
 * Initial values returned by `GET /pool_shippings/new` — the shipping
 * service catalogue the Delivery form renders as options.
 */
export interface NewPoolShippingResponse {
  services: PoolShippingService[];
  /** Per-player fulfilment-queue quota, or `null` on a backend that doesn't
   *  report one yet (the form then behaves exactly as before). */
  quota: ShippingQuota | null;
}

/**
 * How many cards this player may still hand to fulfilment. The operator caps
 * how many of one player's cards may sit in the queue at once; `remaining` is
 * what is left of that cap right now, and it frees up as deliveries ship.
 */
export interface ShippingQuota {
  /** The operator's cap on cards in the queue at once. */
  limit: number;
  /** Cards already awaiting fulfilment for this player. */
  inProcessing: number;
  /** Cards the player may still add — `limit - inProcessing`, never negative. */
  remaining: number;
}

/** Reason codes the backend attaches to a required protection — why
 *  insurance / signature is mandated. `'fmv'` = an item's fair-market
 *  value crosses the threshold; `'card_count'` = the shipment has too many
 *  cards. Widened with `string & {}` so a new reason never breaks parsing. */
export type ProtectionReason = 'fmv' | 'card_count' | (string & {});

/** Protection requirement the backend derives from the *items* in the
 *  shipment (NOT the address) — so it is available from the fees endpoint
 *  even before an address is picked. Drives auto-select, the "Required"
 *  badge, the opt-out warning, and the high-value disclaimer. */
export interface ShippingProtection {
  /** Insurance is mandated for this shipment. */
  requiredInsurance: boolean;
  /** Signature confirmation is mandated for this shipment. */
  requiredSignature: boolean;
  /** `code_name` of the shipping option that satisfies the requirement —
   *  the one the form should auto-select. */
  requiredCodeName: ShippingServiceCodeName;
  /** Why insurance is required (e.g. `['fmv']`), or null when it isn't. */
  insuranceReason: ProtectionReason[] | null;
  /** Why signature is required, or null when it isn't. */
  signatureReason: ProtectionReason[] | null;
  /** A single card exceeds the max insurable value → show the high-value
   *  disclaimer. */
  singleCardOverMax: boolean;
  /** Insurance coverage was capped at `maxInsurableValue`. */
  insuranceCapped: boolean;
  /** The cap, in USD (e.g. 5000). */
  maxInsurableValue: number;
}

/** Split of `shippingFee` for the selected option: carrier `base` rate,
 *  declared-value `insurance` and the `signature` surcharge — each 0 when the
 *  option doesn't include it, and base + insurance + signature equals
 *  `shippingFee`. Null exactly when `shippingFee` is null (not quotable yet)
 *  and on older backends that don't send the split. */
export interface ShippingFeeComponents {
  base: number;
  insurance: number;
  signature: number;
}

/** The regular figures a promotion replaced on a fee quote. Every `original*`
 *  is null when its fee is not quotable yet (no address); `discount` is what
 *  the promotion takes off this quote, and 0 when it lowers only other options. */
export interface ShippingFeesPromo {
  id: number;
  label: string;
  startsAt: string | null;
  endsAt: string | null;
  originalShippingFee: number | null;
  originalShippingFeeComponents: ShippingFeeComponents | null;
  originalHandlingFee: number | null;
  /** Regular price per Delivery Shipping Option, keyed by service id. */
  originalOptionPrices: Record<number, number>;
  discount: number;
}

/** Fee figures for the reveal Create Shipping modal. `handlingFee` is the
 *  flat handling charge; `shippingFee` / `salesTax` stay null until a
 *  delivery address is known (address-dependent quote). `note` is the
 *  backend-generated confirm-by / auto-buyback warning copy (deadline +
 *  buyback % filled in from the operator's config) — null when auto-sell is
 *  disabled. */
export interface ShippingFees {
  handlingFee: number | null;
  shippingFee: number | null;
  /** Breakdown of `shippingFee` (base / insurance / signature). Null whenever
   *  `shippingFee` is null, or when the backend predates the split. */
  shippingFeeComponents: ShippingFeeComponents | null;
  salesTax: number | null;
  /** What the player is charged: shipping + handling + sales tax. Null while
   *  `shippingFee` is, and on a backend that predates the field. */
  total: number | null;
  /** The operator-funded promotion that priced this quote, or null. Every
   *  figure on this object is then already what the player pays. */
  promo: ShippingFeesPromo | null;
  note: string | null;
  /** Full EasyPost price per Delivery Shipping Option, keyed by service id —
   *  what the radio list shows. Empty until items + address make it quotable. */
  optionPrices: Record<number, number>;
  /** Required-protection summary, derived from the items alone (independent
   *  of address). Null when the backend omits it (older payloads). */
  protection: ShippingProtection | null;
}

/** Optional inputs that unlock the address-dependent figures: `addressId`
 *  enables the NumeralHQ sales-tax quote; `serviceIds` are the chosen
 *  Delivery Shipping Option(s) whose flat price becomes the shipping fee. */
export interface ShippingFeesParams {
  addressId?: number | null;
  serviceIds?: (number | string)[];
}

/** Body params for `POST /pool_shippings`. Maps to the Rails strong-params
 *  + nested-attributes contract. */
export interface CreatePoolShippingParams {
  addressId: number;
  poolItemIds: (number | string)[];
  poolShippingServiceIds: number[];
  /** Player chose a weaker protection than required and explicitly accepted
   *  the risk. Sent as the top-level `acknowledged_opt_out` so the backend
   *  doesn't reject the create with `PROTECTION_ACK_REQUIRED`. Omit when
   *  there is no opt-out. */
  acknowledgedOptOut?: boolean;
  /** The total the player was shown, in the operator currency. Sent as the
   *  top-level `expected_total`: if the server would charge more, it refuses
   *  with 409 `SHIPPING_PRICE_CHANGED` and charges nothing. Omit when there was
   *  no whole total to hold the server to. */
  expectedTotal?: number;
}

interface RawInvoice {
  id?: number | string;
  status?: string | null;
  total_amount?: number | string | null;
  paid_at?: string | null;
}

interface RawShipping {
  id?: number | string;
  status?: string;
  is_allow_to_cancel?: boolean | null;
  settle_summary?: string | null;
  shipping_type?: string;
  humanized_status?: string;
  created_at?: string;
  pool_items_count?: number | string;
  pool_shipping_invoice?: RawInvoice | null;
  preview_image_url?: string | null;
  preview_title?: string | null;
}

interface RawMeta {
  total_count?: number | string;
  page?: number | string;
  per?: number | string;
  active_out_count?: number | string;
}

interface RawPage {
  pool_shippings?: RawShipping[];
  meta?: RawMeta;
}

interface RawShippingService {
  id?: number | string;
  name?: string;
  description?: string | null;
  price?: number | string | null;
  code_name?: string | null;
}

/** `protection` block on the fees payload — snake_case as the backend
 *  emits it. Mapped to `ShippingProtection` (camelCase) by `mapProtection`. */
interface RawProtection {
  required_insurance?: boolean | null;
  required_signature?: boolean | null;
  required_code_name?: string | null;
  insurance_reason?: string[] | null;
  signature_reason?: string[] | null;
  single_card_over_max?: boolean | null;
  insurance_capped?: boolean | null;
  max_insurable_value?: number | string | null;
}

/** Split of an option's full price, as the fees payload carries it. */
interface RawFeeComponents {
  base?: number | string | null;
  insurance?: number | string | null;
  signature?: number | string | null;
}

/** `promo` block on the fees payload, snake_case as the backend emits it. */
interface RawFeesPromo {
  id?: number | string | null;
  label?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  original_shipping_fee?: number | string | null;
  original_shipping_fee_components?: RawFeeComponents | null;
  original_handling_fee?: number | string | null;
  original_option_prices?: Record<string, number | string | null> | null;
  discount?: number | string | null;
}

/** `promo` block of a delivery's `charge_breakdown`. */
interface RawChargePromo {
  id?: number | string | null;
  label?: string | null;
  original_shipping?: number | string | null;
  original_handling?: number | string | null;
  discount?: number | string | null;
}

interface RawCreatedShipping {
  pool_shipping?: RawShipping & { id?: number | string };
}

function mapInvoice(raw: RawInvoice | null | undefined): IframePoolShippingInvoice | null {
  if (!raw || raw.id === undefined || raw.id === null) return null;
  return {
    id: Number(raw.id),
    status: raw.status ?? null,
    totalAmount:
      raw.total_amount !== undefined && raw.total_amount !== null
        ? Number(raw.total_amount)
        : null,
    paidAt: raw.paid_at ?? null,
  };
}

function mapShipping(raw: RawShipping): IframePoolShipping {
  return {
    id: Number(raw.id ?? 0),
    status: (raw.status ?? 'pending') as PoolShippingStatus,
    isAllowToCancel: Boolean(raw.is_allow_to_cancel),
    settleSummary:
      raw.settle_summary === 'refunded' || raw.settle_summary === 'partially_shipped'
        ? raw.settle_summary
        : null,
    shippingType: (raw.shipping_type ?? 'delivery') as PoolShippingType,
    humanizedStatus: raw.humanized_status ?? '',
    createdAt: raw.created_at ?? '',
    poolItemsCount: Number(raw.pool_items_count ?? 0),
    invoice: mapInvoice(raw.pool_shipping_invoice),
    previewImageUrl: raw.preview_image_url ?? null,
    previewTitle: raw.preview_title?.trim() || null,
  };
}

function mapPage(raw: RawPage): IframePoolShippingsPage {
  const shippings = (raw.pool_shippings ?? []).map(mapShipping);
  return {
    shippings,
    meta: {
      totalCount: Number(raw.meta?.total_count ?? shippings.length),
      page: Number(raw.meta?.page ?? 1),
      per: Number(raw.meta?.per ?? shippings.length),
      activeOutCount: Number(raw.meta?.active_out_count ?? 0),
    },
  };
}

function mapService(raw: RawShippingService): PoolShippingService {
  return {
    id: Number(raw.id ?? 0),
    name: raw.name ?? '',
    description: raw.description ?? null,
    price: raw.price !== undefined && raw.price !== null ? Number(raw.price) : null,
    // `code_name` → `codeName`. Default to 'delivery' (no protection) so an
    // older payload that omits it doesn't read as a required tier.
    codeName: (raw.code_name ?? 'delivery') as ShippingServiceCodeName,
  };
}

// Tolerate non-numeric sentinels (e.g. handling's 'Service not found') → null,
// so callers show a placeholder instead of a NaN amount.
function num(v: number | string | null | undefined): number | null {
  if (v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Split of an option's full price. Gate on a numeric `base` so a malformed
// block — or an older backend that omits it — degrades to null and callers
// fall back to the undivided fee.
function mapFeeComponents(raw: RawFeeComponents | null | undefined): ShippingFeeComponents | null {
  const base = num(raw?.base);
  if (!raw || base === null) return null;
  return { base, insurance: num(raw.insurance) ?? 0, signature: num(raw.signature) ?? 0 };
}

function mapOptionPrices(
  raw: Record<string, number | string | null> | null | undefined,
): Record<number, number> {
  const prices: Record<number, number> = {};
  for (const [id, value] of Object.entries(raw ?? {})) {
    const price = num(value);
    if (price !== null) prices[Number(id)] = price;
  }
  return prices;
}

/** A promotion without a readable discount maps to null. A discount of 0 stays:
 *  it lowers other options, whose regular prices the option list strikes through. */
function mapFeesPromo(raw: RawFeesPromo | null | undefined): ShippingFeesPromo | null {
  const discount = num(raw?.discount);
  if (!raw || discount === null || discount < 0) return null;
  return {
    id: Number(raw.id ?? 0),
    label: raw.label ?? '',
    startsAt: raw.starts_at ?? null,
    endsAt: raw.ends_at ?? null,
    originalShippingFee: num(raw.original_shipping_fee),
    originalShippingFeeComponents: mapFeeComponents(raw.original_shipping_fee_components),
    originalHandlingFee: num(raw.original_handling_fee),
    originalOptionPrices: mapOptionPrices(raw.original_option_prices),
    discount,
  };
}

function mapChargePromo(raw: RawChargePromo | null | undefined): ShippingChargePromo | null {
  const discount = num(raw?.discount);
  if (!raw || discount === null || discount <= 0) return null;
  return {
    id: Number(raw.id ?? 0),
    label: raw.label ?? '',
    originalShipping: num(raw.original_shipping),
    originalHandling: num(raw.original_handling),
    discount,
  };
}

/** snake_case `protection` block → camelCase `ShippingProtection`. Returns
 *  null when the backend omits the block (older payload). */
function mapProtection(raw: RawProtection | null | undefined): ShippingProtection | null {
  if (!raw) return null;
  const mapReason = (v: string[] | null | undefined): ProtectionReason[] | null =>
    Array.isArray(v) && v.length > 0 ? (v as ProtectionReason[]) : null;
  return {
    requiredInsurance: Boolean(raw.required_insurance),
    requiredSignature: Boolean(raw.required_signature),
    requiredCodeName: (raw.required_code_name ?? 'delivery') as ShippingServiceCodeName,
    insuranceReason: mapReason(raw.insurance_reason),
    signatureReason: mapReason(raw.signature_reason),
    singleCardOverMax: Boolean(raw.single_card_over_max),
    insuranceCapped: Boolean(raw.insurance_capped),
    maxInsurableValue:
      raw.max_insurable_value !== undefined && raw.max_insurable_value !== null
        ? Number(raw.max_insurable_value)
        : 0,
  };
}

/** GET /pool_shippings — the player's deliveries (Deliveries tab). */
export async function listPoolShippings(
  client: ApiClient,
  params: IframePoolShippingsParams = {},
): Promise<IframePoolShippingsPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await client.request<RawPage | undefined>(`${BASE}${suffix}`, { method: 'GET' });
  return mapPage(raw ?? {});
}

/** One shipped item as the v1 ShowSerializer emits it (PoolItems::ShortSerializer):
 *  `images` is an array of objects; FMV isn't included. */
interface RawShowItem {
  id?: number | string;
  title?: string;
  images?: Array<{ url?: string | null }> | null;
}

/** One position row (PoolShippingPositions::IndexSerializer). */
interface RawShowPosition {
  id?: number | string;
  name?: string;
  description?: string | null;
  price?: number | string | null;
  code_name?: string | null;
}

/** The detail payload under root `pool_shipping` — extends the list-row shape
 *  with the detail-only fields the v1 ShowSerializer adds. */
interface RawShowShipping extends RawShipping {
  address?: RawAddress | null;
  pool_items?: RawShowItem[];
  pool_shipping_positions?: RawShowPosition[];
  /** pool_item_id -> "shipped" | "unavailable" | "pending" (iframe ShowSerializer). */
  item_settle_statuses?: Record<string, string> | null;
  /** Real cost snapshot (operator currency) added by the iframe ShowSerializer.
   *  `insurance` splits the (still full-price) `shipping` figure; absent on
   *  shipments charged before the breakdown existed. */
  charge_breakdown?: {
    shipping?: number | null;
    insurance?: number | null;
    handling?: number | null;
    sales_tax?: number | null;
    total?: number | null;
    /** Set when an operator-funded promotion priced the shipment. */
    promo?: RawChargePromo | null;
  } | null;
  tracking_number?: string | null;
  company_name?: string | null;
}

/**
 * Maps the `Shared::V1::PoolShippings::ShowSerializer` shape (root
 * `pool_shipping`): `address` is a raw Address record (reuse `mapAddress`),
 * items come under `pool_items` with an `images[]` array, and positions under
 * `pool_shipping_positions`. The serializer has no top-level service object,
 * so we synthesize one from the first position (the chosen delivery option) —
 * otherwise the Show page hides the positions block. FMV / sales tax aren't in
 * this payload (not shown on the Show page); total is summed from positions.
 * Shared between the show and cancel endpoints (same response shape).
 */
function mapShowShipping(ps: RawShowShipping): IframePoolShippingDetail {
  const base = mapShipping(ps);

  const settleStatuses = ps.item_settle_statuses ?? {};
  const items: IframePoolShippingItem[] = (ps.pool_items ?? []).map((it) => {
    const id = Number(it.id ?? 0);
    const raw = settleStatuses[String(id)];
    const settleStatus =
      raw === 'shipped' || raw === 'unavailable' || raw === 'pending' || raw === 'canceled'
        ? raw
        : null;
    return {
      id,
      title: it.title ?? '',
      imageUrl: it.images?.[0]?.url ?? null,
      fairMarketValue: null,
      settleStatus,
    };
  });

  // Handling is a separate mandatory fee, shown in the Payment breakdown — not a
  // protection option the player picked, so keep it out of the options list.
  const positions: IframePoolShippingPosition[] = (ps.pool_shipping_positions ?? [])
    .filter((p) => p.code_name !== 'handling')
    .map((p) => ({
      id: Number(p.id ?? 0),
      name: p.name ?? '',
      price: p.price !== undefined && p.price !== null ? Number(p.price) : null,
    }));

  const first = positions[0];
  // The Show serializer carries no service code_name (the chosen tier is
  // already materialized as a position); the Show page never runs the
  // protection logic, so a 'delivery' placeholder is sufficient.
  const service: PoolShippingService | null = first
    ? {
        id: first.id,
        name: first.name,
        description: null,
        price: first.price,
        codeName: 'delivery',
      }
    : null;

  const totalAmount = positions.reduce((sum, p) => sum + (p.price ?? 0), 0);

  const cb = ps.charge_breakdown ?? null;
  const chargeBreakdown: ShippingChargeBreakdown | null = cb
    ? {
        shipping: cb.shipping ?? null,
        insurance: cb.insurance ?? null,
        handling: cb.handling ?? null,
        salesTax: cb.sales_tax ?? null,
        total: cb.total ?? null,
        promo: mapChargePromo(cb.promo),
      }
    : null;

  return {
    ...base,
    poolItemsCount: items.length,
    address: ps.address ? mapAddress(ps.address) : null,
    items,
    service,
    positions,
    salesTax: 0,
    totalAmount,
    chargeBreakdown,
    trackingNumber: ps.tracking_number ?? null,
    companyName: ps.company_name ?? null,
  };
}

/** GET /pool_shippings/:id — detail for the Delivery Show page. */
export async function getPoolShipping(
  client: ApiClient,
  id: number,
): Promise<IframePoolShippingDetail> {
  const raw = await client.request<{ pool_shipping?: RawShowShipping } | undefined>(
    `${BASE}/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  return mapShowShipping(raw?.pool_shipping ?? {});
}

/**
 * PUT /pool_shippings/:id/cancel — cancel a paid delivery. The backend buys
 * every card back at the regular buyback price and refunds the shipping +
 * handling charges to the wallet, then returns the updated detail (same
 * shape as show). Errors (422):
 *   `SHIPPING_NOT_CANCELABLE` — the window closed, fulfilment already started;
 *   `SHIPPING_CANCEL_FAILED`  — transient, safe to retry with another call.
 */
export async function cancelPoolShipping(
  client: ApiClient,
  id: number,
): Promise<IframePoolShippingDetail> {
  const raw = await client.request<{ pool_shipping?: RawShowShipping } | undefined>(
    `${BASE}/${encodeURIComponent(String(id))}/cancel`,
    { method: 'PUT' },
  );
  return mapShowShipping(raw?.pool_shipping ?? {});
}

/** GET /pool_shippings/new — shipping-service catalogue for the form. */
export async function getNewShipping(client: ApiClient): Promise<NewPoolShippingResponse> {
  const raw = await client.request<
    | {
        result?: {
          services?: RawShippingService[];
          shipping_items_limit?: number | null;
          items_in_processing?: number | null;
          remaining_items?: number | null;
        };
      }
    | undefined
  >(`${BASE}/new`, { method: 'GET' });
  const services = (raw?.result?.services ?? []).map(mapService);
  return { services, quota: mapQuota(raw?.result) };
}

/** Quota block of `GET /pool_shippings/new`. Absent (older backend) → `null`,
 *  which every caller reads as "no cap to show". */
function mapQuota(result?: {
  shipping_items_limit?: number | null;
  items_in_processing?: number | null;
  remaining_items?: number | null;
}): ShippingQuota | null {
  if (result?.shipping_items_limit == null || result.remaining_items == null) return null;
  return {
    limit: Number(result.shipping_items_limit),
    inProcessing: Number(result.items_in_processing ?? 0),
    remaining: Number(result.remaining_items),
  };
}

/**
 * GET /pool_shippings/fees?pool_item_ids[]=… — fee figures for the reveal
 * modal, quoted for the given items. Today the backend returns the
 * `handling_fee` (per-item × count); `shipping_fee` / `sales_tax` arrive once
 * the endpoint also takes a delivery address.
 */
export async function getShippingFees(
  client: ApiClient,
  poolItemIds: (number | string)[] = [],
  params: ShippingFeesParams = {},
): Promise<ShippingFees> {
  const qs = new URLSearchParams();
  for (const id of poolItemIds) qs.append('pool_item_ids[]', String(id));
  // address unlocks the NumeralHQ sales-tax quote; service ids price the
  // shipping fee. Both optional — without them the backend returns "TBD".
  if (params.addressId != null) qs.set('address_id', String(params.addressId));
  for (const id of params.serviceIds ?? [])
    qs.append('pool_shipping_service_ids[]', String(id));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await client.request<
    | {
        result?: {
          handling_fee?: number | string | null;
          shipping_fee?: number | string | null;
          shipping_fee_components?: RawFeeComponents | null;
          sales_tax?: number | string | null;
          total?: number | string | null;
          promo?: RawFeesPromo | null;
          note?: string | null;
          option_prices?: Record<string, number | string | null> | null;
          protection?: RawProtection | null;
        };
      }
    | undefined
  >(`${BASE}/fees${suffix}`, { method: 'GET' });
  const r = raw?.result;
  return {
    handlingFee: num(r?.handling_fee),
    shippingFee: num(r?.shipping_fee),
    shippingFeeComponents: mapFeeComponents(r?.shipping_fee_components),
    salesTax: num(r?.sales_tax),
    total: num(r?.total),
    promo: mapFeesPromo(r?.promo),
    note: r?.note ?? null,
    optionPrices: mapOptionPrices(r?.option_prices),
    // Items-derived (address-independent), so it's present even before an
    // address is picked. Drives auto-select + opt-out + disclaimer.
    protection: mapProtection(r?.protection),
  };
}

/**
 * POST /pool_shippings — create a delivery from the chosen address, items
 * and service. Maps the flat iframe params onto the Rails nested-attributes
 * contract (`pool_shipping_items_attributes` / `pool_shipping_positions_attributes`).
 */
export async function createPoolShipping(
  client: ApiClient,
  params: CreatePoolShippingParams,
): Promise<IframePoolShipping> {
  const body = {
    pool_shipping: {
      address_id: params.addressId,
      pool_shipping_items_attributes: params.poolItemIds.map((poolItemId) => ({
        pool_item_id: poolItemId,
      })),
      pool_shipping_positions_attributes: params.poolShippingServiceIds.map(
        (poolShippingServiceId) => ({ pool_shipping_service_id: poolShippingServiceId }),
      ),
    },
    // Top-level (sibling of `pool_shipping`, NOT nested): the opt-out
    // acknowledgement. Sent only when the player removed a required
    // protection; otherwise omitted entirely.
    ...(params.acknowledgedOptOut !== undefined
      ? { acknowledged_opt_out: params.acknowledgedOptOut }
      : {}),
    // Also top-level: the price the player confirmed, refused if it has risen.
    ...(params.expectedTotal !== undefined ? { expected_total: params.expectedTotal } : {}),
  };
  const raw = await client.request<RawCreatedShipping | undefined>(BASE, {
    method: 'POST',
    body,
  });
  return mapShipping(raw?.pool_shipping ?? {});
}
